import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  LOGO_ICON,
  MEMO_BANNER_IMAGE,
  MENU_BACKGROUND_IMAGE,
  SMALL_LOGO,
} from '@careapp-group/core';

@Component({
    selector: 'ca-branding',
    templateUrl: './branding.component.html',
    styleUrls: ['./branding.component.scss'],
    standalone: false
})
export class BrandingComponent {
  public SMALL_LOGO = SMALL_LOGO;
  public MEMO_BANNER_IMAGE = MEMO_BANNER_IMAGE;
  public MENU_BACKGROUND_IMAGE = MENU_BACKGROUND_IMAGE;
  public LOGO_ICON = LOGO_ICON;
  private WHITE_COLOUR = '#ffffff';

  @Input() set readOnly(value: boolean) {
    this._readOnly = value;
    if (!!value && this.branding.enabled) {
      this.branding.disable();
    }
  }

  @Output()
  colorsSubmitted = new EventEmitter();
  @Output()
  imageSubmitted = new EventEmitter();
  @Output()
  imageRemoved = new EventEmitter();
  @Output()
  colorsReset = new EventEmitter();

  readonly INCLUDE_COLORS = true;
  readonly INCLUDE_PLACEHOLDER = false;

  _readOnly: boolean = false;

  @Input()
  set branding(value: any) {
    if (value) {
      const patch = { ...value };
      this._branding.get('media').reset();
      patch.media = value.media.reduce((prev, next) => ({ ...prev, [next.purpose]: next }), {});
      this._branding.patchValue(patch);
    }
  }

  get branding() {
    return this._branding;
  }

  get primary() {
    return this._branding.get('colors').get('primary').value;
  }

  valueOrDefault(value) {
    return value != null && value !== '' ? value : this.WHITE_COLOUR;
  }

  // The server has a secondary colour that is a legacy property
  // from a long time ago. We are currently not using the secondary
  // brand colour, but might in the future. So to prevent
  // things turning horribly ugly if we decide to use the secondary
  // colour in the app, and have these custom colour schemes
  // that don't know about it, we are simply going to
  // set the primary and secondary to the same thing.
  set primary(value) {
    value = this.valueOrDefault(value);
    this._branding.get('colors').get('primary').setValue(value);
    this._branding.get('colors').get('secondary').setValue(value);
  }

  get secondary() {
    return this._branding.get('colors').get('primary').value;
  }

  get fabForeground() {
    return this._branding.get('colors').get('fabForeground').value;
  }

  set fabForeground(value) {
    value = this.valueOrDefault(value);
    this.branding.get('colors').get('fabForeground').setValue(value);
  }

  get fabBackground() {
    return this._branding.get('colors').get('fabBackground').value;
  }

  set fabBackground(value) {
    value = this.valueOrDefault(value);
    this.branding.get('colors').get('fabBackground').setValue(value);
  }
  get fabShadow() {
    return this._branding.get('colors').get('fabShadow').value;
  }

  set fabShadow(value) {
    value = this.valueOrDefault(value);
    this.branding.get('colors').get('fabShadow').setValue(value);
  }

  ngOnInit() {
    if (!!this.branding) {
      this.branding.valueChanges.subscribe((changes) => {
        if (this._readOnly && this._branding.enabled) {
          this._branding.disable();
        }
      });
    }
  }

  _branding = new UntypedFormGroup({
    colors: new UntypedFormGroup({
      primary: new UntypedFormControl(''),
      secondary: new UntypedFormControl(''),
      fabForeground: new UntypedFormControl(''),
      fabBackground: new UntypedFormControl(''),
      fabShadow: new UntypedFormControl(''),
    }),
    media: new UntypedFormGroup({
      [SMALL_LOGO]: new UntypedFormControl('', Validators.required),
      [MEMO_BANNER_IMAGE]: new UntypedFormControl('', Validators.required),
      [MENU_BACKGROUND_IMAGE]: new UntypedFormControl('', Validators.required),
      [LOGO_ICON]: new UntypedFormControl('', Validators.required),
    }),
  });

  onSubmitColors() {
    this.colorsSubmitted.emit(this.branding.value.colors);
  }

  resetColors() {
    this.colorsReset.emit();
  }

  resetImage(controlName: string) {
    this.imageRemoved.emit(this._branding.get('media').get(controlName).value);
  }

  onImageSubmitted(purpose: string) {
    if (
      this.branding.get('media').get(purpose) &&
      this.branding.get('media').get(purpose).value &&
      this.branding.get('media').get(purpose).valid
    ) {
      if (this.branding.get('media').get(purpose).value.purpose) {
        // Image has already been saved/used
        return;
      }
      this.imageSubmitted.emit({ ...this.branding.get('media').get(purpose).value, purpose });
    }
  }
}
